import React, { useEffect } from 'react';
import _isEqual from 'lodash/isEqual';
import _debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import {
  CDataTable,
  CPagination,
  CFormGroup,
  CInputCheckbox,
  CLabel,
} from '@coreui/react';
import { generateID, transformOrderFromAscToBoolean, transformOrderFromBooleanToAsc } from '../../core/helpers/default';
import './styles.scss';

const propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape()),
  loading: PropTypes.bool,
  pages: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  columnFilter: PropTypes.oneOfType([PropTypes.shape(), PropTypes.bool]),
  columnFilterValue: PropTypes.shape(),
  onColumnFilterChange: PropTypes.func,
  tableFilterValue: PropTypes.string,
  onTableFilterChange: PropTypes.func,
  sorter: PropTypes.shape({
    sort: PropTypes.string,
    order: PropTypes.oneOf(['asc', 'desc']),
  }),
  onSorterChange: PropTypes.func,
  itemsPerPageValue: PropTypes.number,
  onItemsPerPageChange: PropTypes.func,
  onCustomSelectClick: PropTypes.func,
  fields: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onRowCheck: PropTypes.func,
  onSelectAll: PropTypes.func,
  onRowClick: PropTypes.func,
  selectType: PropTypes.oneOf(['checkbox', 'radio']),
  scopedSlots: PropTypes.shape(),
  columnHeaderSlot: PropTypes.shape(),
  total: PropTypes.number,
  from: PropTypes.number,
  to: PropTypes.number,
  itemsPerPageSelect: PropTypes.oneOfType([PropTypes.shape(), PropTypes.bool]),
  preserveSelectedFilesAfterRefetch: PropTypes.bool,
};

const defaultProps = {
  data: [],
  loading: false,
  pages: 1,
  currentPage: 1,
  total: 0,
  from: 0,
  to: 0,
  onPageChange: () => {},
  columnFilter: false,
  columnFilterValue: null,
  onColumnFilterChange: () => {},
  tableFilterValue: '',
  onTableFilterChange: () => {},
  sorter: {},
  onSorterChange: () => {},
  itemsPerPageValue: 10,
  onItemsPerPageChange: () => {},
  onCustomSelectClick: () => {},
  onRowCheck: null,
  onSelectAll: null,
  selectType: 'checkbox',
  scopedSlots: {},
  columnHeaderSlot: {},
  itemsPerPageSelect: false,
  onRowClick: undefined,
  preserveSelectedFilesAfterRefetch: false,
};

const ServerSideTableAdvanced = ({
  data, loading, pages, fields, onRowCheck, selectType,
  currentPage, onPageChange,
  columnFilter, columnFilterValue, onColumnFilterChange,
  tableFilterValue,
  onTableFilterChange,
  sorter, onSorterChange,
  itemsPerPageValue, onItemsPerPageChange,
  scopedSlots, columnHeaderSlot, onSelectAll, total, from, to,
  itemsPerPageSelect, onRowClick, onCustomSelectClick,
  preserveSelectedFilesAfterRefetch,
}) => {
  useEffect(() => {
    if (!loading && onSelectAll && !preserveSelectedFilesAfterRefetch) onSelectAll(false);
    // eslint-disable-next-line
  }, [loading, preserveSelectedFilesAfterRefetch]);

  const tableFields = onRowCheck ? [{ key: 'select', label: '', filter: false }, ...fields] : fields;
  const tableID = generateID();

  const handleTableFilterChange = _debounce((value) => {
    onTableFilterChange(value);
  }, 500);

  const sorterValue = sorter.sort
    ? { column: sorter.sort, asc: transformOrderFromAscToBoolean(sorter.order) } : null;

  const handleSorterChange = (value) => {
    if (_isEqual(value, sorterValue)) return;
    onSorterChange(value.column, transformOrderFromBooleanToAsc(value.asc));
  };

  const handleGoToPageKeyDown = (event) => {
    if (event.key === 'Enter') {
      const page = Number(event.target.value);
      event.target.value = '';
      if (page > 0) onPageChange(page);
    }
  };

  return (
    <>
      <CDataTable
        className="server-side-table"
        items={data}
        fields={tableFields}
        cleaner
        hover
        onRowClick={onRowClick}
        sorter={{ external: true }}
        loading={loading}
        columnFilter={columnFilter}
        columnFilterValue={columnFilterValue}
        onColumnFilterChange={onColumnFilterChange}
        tableFilter={{ external: true, placeholder: 'Search by...', label: ' ' }}
        onTableFilterChange={handleTableFilterChange}
        sorterValue={sorterValue}
        onSorterValueChange={handleSorterChange}
        itemsPerPage={itemsPerPageValue}
        onPaginationChange={onItemsPerPageChange}
        itemsPerPageSelect={itemsPerPageSelect}
        columnHeaderSlot={{
          ...columnHeaderSlot,
          select: onSelectAll ? (
            <CFormGroup variant={`custom-${selectType}`} style={{ position: 'relative', left: '-0.75rem' }}>
              <CInputCheckbox
                custom
                id={`checkbox${tableID}`}
                onChange={(event) => onSelectAll(event.target.checked)}
              />
              <CLabel
                style={{ cursor: 'pointer' }}
                variant="custom-checkbox"
                htmlFor={`checkbox${tableID}`}
              />
            </CFormGroup>
          ) : null,
        }}
        scopedSlots={{
          ...scopedSlots,
          select: (item) => (
            <td>
              <CFormGroup variant={`custom-${selectType}`} onClick={(e) => onCustomSelectClick(e, item.id)}>
                <>
                  <CInputCheckbox
                    custom
                    id={`checkbox${tableID}${item.id}`}
                    checked={item._selected}
                    onChange={(e) => onRowCheck(e, item.id)}
                  />
                  <CLabel
                    style={{ cursor: 'pointer' }}
                    variant="custom-checkbox"
                    htmlFor={`checkbox${tableID}${item.id}`}
                  />
                </>
              </CFormGroup>
            </td>
          ),
        }}
      />
      <div className="d-flex align-items-center justify-content-center justify-content-md-between flex-wrap">
        <div className="pagination-container">
          {pages > 1
            && (
            <>
              <CPagination
                pages={pages}
                activePage={currentPage}
                onActivePageChange={onPageChange}
              />
              <input
                type="number"
                className="form-control sm:ml-2 go-to-page"
                placeholder="Go to page"
                onKeyDown={handleGoToPageKeyDown}
              />
            </>
            )}
        </div>
        {!!total && !!from && !!to && (
          <div className="table-total">
            <div>
              <strong>
                {`${from} to ${to}`}
              </strong>
              {' '}
              of
              {' '}
              <strong>
                {total}
              </strong>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

ServerSideTableAdvanced.propTypes = propTypes;
ServerSideTableAdvanced.defaultProps = defaultProps;

export default ServerSideTableAdvanced;
